// The browser preview (live.syqel.com): the real engine + full world catalog
// on synthetic music (or the visitor's mic), permanently watermarked, no
// login, no persistence — a funnel to the desktop app. Locked decision:
// "browser build = watermarked, no-login preview only."
//
// Self-contained on purpose: no Tauri IPC exists here, so nothing from
// src/ that talks to the shell may run. The only desktop import is the
// world catalog filter (src/worlds.ts — module-scope safe in a browser).

import type * as EngineT from "@syqel/engine";
import { Engine, otaModeFactory } from "@syqel/ota-client";
import { getAssetBase, setAssetBase } from "@syqel/worlds-three";
import { useEffect, useRef, useState } from "react";
import { appWorlds } from "../src/worlds";
// The desktop free tier's watermark, burned into the framebuffer — a DOM div
// is two DevTools clicks from a clean feed; pixels are not.
import { drawWatermark } from "../src/world/watermark";
import { initAnalytics, track } from "./analytics";

// Same-origin assets by default (models/env ship in the deploy); the env
// override lets `pnpm dev:web` reuse the syqel-art dev asset server.
if (!getAssetBase()) setAssetBase(import.meta.env.VITE_ASSET_BASE ?? "");
initAnalytics();

const WIDTH = 1920;
const HEIGHT = 1080;
// The family download hub (dedicated landing page with direct links for
// Live / Art / Desktop / Studio — page itself is a queued task).
const DOWNLOAD_URL = "https://download.syqel.com";

// No PerfGovernor here — a fixed ladder stepped by measured fps keeps the
// preview smooth on laptops without importing the desktop perf stack.
const QUALITY_STEPS = [1, 0.84, 0.7, 0.58, 0.5];

export function PreviewApp() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [webglOk] = useState(
    () => document.createElement("canvas").getContext("webgl2") !== null,
  );
  const [worldName, setWorldName] = useState("");
  const [micOn, setMicOn] = useState(false);
  // True once the GL-burned watermark owns the job; the .pv-wm div then stays
  // out of the DOM entirely instead of doubling the mark.
  const [burned, setBurned] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const micOnRef = useRef(false);
  const stereoRef = useRef<EngineT.StereoAnalyser | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const transitionRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !webglOk) return;
    canvas.width = WIDTH;
    canvas.height = HEIGHT;

    const music = new Engine.MusicalAnalyzer();
    const synth = new Engine.SyntheticMusic();
    const director = new Engine.Director({
      minHoldSec: 16,
      maxHoldSec: 45,
      transitionSec: 3,
      firstHoldSec: 10,
      pool: appWorlds(),
    });
    transitionRef.current = () => director.requestTransition();

    const host = new Engine.VisualHost({
      modeFactory: otaModeFactory,
      onModeError: (key, err) => console.warn(`[preview] renderer '${key}' failed:`, err),
    });
    host.attach(canvas);
    // The host's own context (getContext returns the existing one). PostFX can
    // fall back to a 2D blit, in which case this is null and the DOM watermark
    // below remains the gate.
    const gl = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
    if (gl) setBurned(true);

    let raf = 0;
    let disposed = false;
    let last = performance.now();
    let shownWorld = "";
    // fps → quality ladder (hysteresis: two fast windows before stepping up)
    let qIndex = 0;
    let frames = 0;
    let windowStart = last;
    let fastWindows = 0;

    const step = () => {
      if (disposed) return;
      raf = requestAnimationFrame(step);
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = now / 1000;

      frames++;
      const winMs = now - windowStart;
      if (winMs > 2500) {
        const fps = (frames * 1000) / winMs;
        if (fps < 45 && qIndex < QUALITY_STEPS.length - 1) {
          qIndex++;
          fastWindows = 0;
        } else if (fps > 56 && qIndex > 0 && ++fastWindows >= 2) {
          qIndex--;
          fastWindows = 0;
        }
        frames = 0;
        windowStart = now;
      }

      try {
        const frame =
          micOnRef.current && stereoRef.current
            ? music.updateFromAnalyser(stereoRef.current, t)
            : synth.frame(t);
        const dir = director.update(frame, dt);
        const q = QUALITY_STEPS[qIndex];
        host.update(frame, dir, dt, t, Math.round((WIDTH * q) / 4) * 4, Math.round((HEIGHT * q) / 4) * 4);
        host.render();
        if (gl) drawWatermark(gl, canvas.width, canvas.height);
        if (dir.world.name !== shownWorld) {
          shownWorld = dir.world.name;
          setWorldName(shownWorld);
        }
      } catch (e) {
        console.error("[preview] loop died:", e);
        disposed = true;
      }
    };
    raf = requestAnimationFrame(step);

    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "KeyN") {
        e.preventDefault();
        director.requestTransition();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      host.dispose();
    };
  }, [webglOk]);

  // Mic teardown on unmount only (StrictMode double-mount safe: the stream
  // lives in refs, not the render effect).
  useEffect(
    () => () => {
      stereoRef.current?.detach();
      micStreamRef.current?.getTracks().forEach((tr) => tr.stop());
    },
    [],
  );

  const toggleMic = async () => {
    if (micOnRef.current) {
      micOnRef.current = false;
      setMicOn(false);
      stereoRef.current?.detach();
      micStreamRef.current?.getTracks().forEach((tr) => tr.stop());
      micStreamRef.current = null;
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      stereoRef.current ??= new Engine.StereoAnalyser();
      stereoRef.current.attachStream(stream);
      micStreamRef.current = stream;
      micOnRef.current = true;
      setMicOn(true);
      setMicError(null);
      track("mic_enabled");
    } catch {
      setMicError("Mic unavailable — running the built-in groove instead.");
      track("mic_denied");
    }
  };

  if (!webglOk) {
    return (
      <div className="pv-fallback">
        <h1>SYQEL LIVE</h1>
        <p>
          This browser can't run the live preview (WebGL2 required) — the desktop app brings its
          own renderer and runs fully offline.
        </p>
        <a className="pv-cta" href={DOWNLOAD_URL} onClick={() => track("get_app_clicked")}>
          Get SYQEL Live for Mac &amp; Windows
        </a>
      </div>
    );
  }

  return (
    <div className="pv">
      <canvas ref={canvasRef} className="pv-canvas" />
      {!burned && <div className="pv-wm">SYQEL</div>}

      <header className="pv-top">
        <div className="pv-brand">
          SYQEL <b>LIVE</b> <span className="pv-chip">PREVIEW</span>
        </div>
        <a className="pv-cta" href={DOWNLOAD_URL} onClick={() => track("get_app_clicked")}>
          Get the app
        </a>
      </header>

      <footer className="pv-bottom">
        <div className="pv-world">
          <span className="pv-k">NOW</span> {worldName || "…"}
        </div>
        <div className="pv-actions">
          <button
            onClick={() => {
              track("next_world_clicked");
              transitionRef.current();
            }}
          >
            Next world
          </button>
          <button className={micOn ? "on" : ""} onClick={() => void toggleMic()}>
            {micOn ? "● Listening to your mic" : "React to my music"}
          </button>
        </div>
        <p className="pv-note">
          {micError ?? "Watermarked browser preview."} The desktop app runs fully offline at
          1080p60 with NDI · Syphon · Spout · MIDI — and no watermark on paid tiers.
        </p>
      </footer>
    </div>
  );
}
